import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axiosConfig from 'src/Resources/axiosConfig';
import { I_Folder, I_Installation } from 'src/interfaces/InstallationTypes';
import { TokenContext } from './tokenContext';
import routes from '../Resources/routes.json';
import { useNavigate } from 'react-router-dom';

export const InstallationsContext = createContext(null);

const InstallationsContextProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const [
    salimax_or_sodistore_Installations,
    setSalimax_Or_Sodistore_Installations
  ] = useState<I_Installation[]>([]);
  const [salidomoInstallations, setSalidomoInstallations] = useState<
    I_Installation[]
  >([]);
  const [sodiohomeInstallations, setSodiohomeInstallations] = useState<
    I_Installation[]
  >([]);
  const [sodistoreGridInstallations, setSodistoreGridInstallations] = useState<
    I_Installation[]
  >([]);
  const [sodistoreProInstallations, setSodistoreProInstallations] = useState<
    I_Installation[]
  >([]);
  const [foldersAndInstallations, setFoldersAndInstallations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updated, setUpdated] = useState(false);
  const navigate = useNavigate();
  const tokencontext = useContext(TokenContext);
  const { removeToken } = tokencontext;
  const socket = useRef<WebSocket | null>(null); // Using useRef instead of useState

  //Store pending updates and apply them in batches
  const pendingUpdates = useRef<
    Record<number, { status: number; testingMode: boolean }>
  >({});

  const updateInstallationStatus = useCallback((id, status, testingMode) => {
    //Insert the incoming message to the pendingUpdates map
    pendingUpdates.current[id] = { status: Number(status), testingMode }; // Ensure status is a number
  }, []);

  //This function will be called every 1 minute and it will update only the installations for which the status and/or the testingMOde has been changed
  const applyBatchUpdates = useCallback(() => {
    if (Object.keys(pendingUpdates.current).length === 0) return; // No updates to apply

    const updatedSalidomo = salidomoInstallations.map((installation) => {
      const update = pendingUpdates.current[installation.id];
      return update
        ? {
            ...installation,
            status: update.status,
            testingMode: update.testingMode
          }
        : installation;
    });

    const updatedSalimax = salimax_or_sodistore_Installations.map(
      (installation) => {
        const update = pendingUpdates.current[installation.id];
        return update
          ? {
              ...installation,
              status: update.status,
              testingMode: update.testingMode
            }
          : installation;
      }
    );

    const updatedSodiohome = sodiohomeInstallations.map((installation) => {
      const update = pendingUpdates.current[installation.id];
      return update
        ? {
            ...installation,
            status: update.status,
            testingMode: update.testingMode
          }
        : installation;
    });

    const updatedSodistoreGrid = sodistoreGridInstallations.map(
      (installation) => {
        const update = pendingUpdates.current[installation.id];
        return update
          ? {
              ...installation,
              status: update.status,
              testingMode: update.testingMode
            }
          : installation;
      }
    );

    const updatedSodistorePro = sodistoreProInstallations.map(
      (installation) => {
        const update = pendingUpdates.current[installation.id];
        return update
          ? {
              ...installation,
              status: update.status,
              testingMode: update.testingMode
            }
          : installation;
      }
    );

    setSalidomoInstallations(updatedSalidomo);
    setSalimax_Or_Sodistore_Installations(updatedSalimax);
    setSodiohomeInstallations(updatedSodiohome);
    setSodistoreGridInstallations(updatedSodistoreGrid);
    setSodistoreProInstallations(updatedSodistorePro);

    // Clear the pending updates after applying
    pendingUpdates.current = {};
  }, [
    salidomoInstallations,
    salimax_or_sodistore_Installations,
    sodiohomeInstallations,
    sodistoreGridInstallations,
    sodistoreProInstallations
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      applyBatchUpdates();
    }, 2000);

    return () => clearInterval(timer); // Cleanup timer on component unmount
  }, [applyBatchUpdates]);

  const openSocket = (installationsToSend) => {
    if (socket.current) {
      socket.current.close();
      socket.current = null;
    }
    const tokenString = localStorage.getItem('token') || sessionStorage.getItem('token');
    const token = tokenString !== null ? tokenString : '';
    const urlWithToken = `wss://monitor.inesco.energy/api/CreateWebSocket?authToken=${token}`;

    const new_socket = new WebSocket(urlWithToken);

    new_socket.addEventListener('open', () => {
      // Send the corresponding installation IDs to the backend
      new_socket.send(
        JSON.stringify(
          installationsToSend.map((installation) => installation.id)
        )
      );
    });

    // Periodically send ping messages to keep the connection alive
    const pingInterval = setInterval(() => {
      if (new_socket.readyState === WebSocket.OPEN) {
        new_socket.send(JSON.stringify([-1]));
      }
    }, 10000);

    new_socket.addEventListener('message', (event) => {
      const message = JSON.parse(event.data); // Parse the JSON data

      // Initial batch from backend is an array, subsequent updates are single objects
      if (Array.isArray(message)) {
        message.forEach((msg) => {
          if (msg.id !== -1) {
            updateInstallationStatus(msg.id, msg.status, msg.testingMode);
          }
        });
      } else if (message.id !== -1) {
        updateInstallationStatus(
          message.id,
          message.status,
          message.testingMode
        );
      }
    });

    new_socket.addEventListener('close', () => {
      clearInterval(pingInterval); // Cleanup ping interval on socket close
      //socket.current = null;
    });

    socket.current = new_socket;
  };

  const closeSocket = () => socket?.current?.close();

  // Function to fetch installations and manage the socket
  const fetchAllInstallations = useCallback(
    async (product: number, open_socket: boolean = true) => {
      try {
        const res = await axiosConfig.get(
          `/GetAllInstallationsFromProduct?product=${product}`
        );

        if (product === 2) {
          setSodiohomeInstallations(res.data);
        } else if (product === 5) {
          setSodistoreProInstallations(res.data);
        } else if (product === 1) {
          setSalidomoInstallations(res.data);
        } else if (product === 0 || product === 3) {
          setSalimax_Or_Sodistore_Installations(res.data);
        } else if (product === 4) {
          setSodistoreGridInstallations(res.data);
        }

        if (open_socket) {
          openSocket(res.data);
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
      }
    },
    [navigate, removeToken]
  );

  const fetchAllFoldersAndInstallations = useCallback(
    async (product: number) => {
      await axiosConfig
        .get(`/GetAllFoldersAndInstallations?productId=${product}`)
        .then((res) => setFoldersAndInstallations(res.data))
        .catch((err) => {
          if (err.response?.status === 401) {
            removeToken();
            navigate(routes.login);
          }
        });
    },
    [navigate, removeToken]
  );

  const createInstallation = useCallback(
    async (formValues: Partial<I_Installation>) => {
      try {
        // wait for the POST to finish
        const res = await axiosConfig.post('/CreateInstallation', formValues);
        const product = formValues.product; // or res.data.product
        await Promise.resolve(fetchAllFoldersAndInstallations(product));
        await Promise.resolve(fetchAllInstallations(product));
        setLoading(false);

        return res.data; // let callers await this if they want
      } catch (error: any) {
        setError(true);
        setLoading(false);
        if (error.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
      }
    },
    [navigate, removeToken, fetchAllFoldersAndInstallations]
  );

  const updateInstallation = useCallback(
    async (formValues: I_Installation, view: string) => {
      try {
        const res = await axiosConfig.put('/UpdateInstallation', formValues);

        setUpdated(true);
        setLoading(false);
        const product = formValues.product; // or res.data.product
        await Promise.resolve(fetchAllFoldersAndInstallations(product));
        await Promise.resolve(fetchAllInstallations(product));
        setTimeout(() => setUpdated(false), 3000);
      } catch (error) {
        setError(true);
        setLoading(false);
        if (error.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
      }
    },
    [
      fetchAllFoldersAndInstallations,
      fetchAllInstallations,
      navigate,
      removeToken
    ]
  );

  const deleteInstallation = useCallback(
    async (formValues: I_Installation, view: string) => {
      try {
        // wait for the POST to finish
        const res = await axiosConfig.delete(
          `/DeleteInstallation?installationId=${formValues.id}`
        );
        setUpdated(true);
        setTimeout(() => setUpdated(false), 3000);
        setLoading(false);

        const product = formValues.product; // or res.data.product
        await Promise.resolve(fetchAllFoldersAndInstallations(product));
        await Promise.resolve(fetchAllInstallations(product));

        return res.data; // let callers await this if they want
      } catch (error: any) {
        setError(true);
        setLoading(false);
        if (error.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
      }
    },
    [
      navigate,
      removeToken,
      sodiohomeInstallations,
      fetchAllFoldersAndInstallations,
      fetchAllInstallations
    ]
  );

  // Returns the HTTP status on failure (null on success) so the caller can tell a duplicate name
  // (409) apart from a real error. Previously every failure was swallowed and the caller closed its
  // modal regardless, which since CreateFolder started refusing same-named siblings would make a
  // rejected create look like it had worked.
  const createFolder = useCallback(
    async (formValues: Partial<I_Folder>, product: number): Promise<number | null> => {
      try {
        await axiosConfig.post('/CreateFolder', formValues);

        setLoading(false);
        await Promise.resolve(fetchAllFoldersAndInstallations(product));
        return null;
      } catch (error) {
        // Was left true on the error path, so a failed create hung the tree on its spinner.
        setLoading(false);
        setError(true);
        if (error.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
        return error.response?.status ?? 0;
      }
    },
    [fetchAllFoldersAndInstallations, navigate, removeToken]
  );

  const updateFolder = useCallback(
    async (formValues: I_Folder, product: number) => {
      try {
        const res = await axiosConfig.put('/UpdateFolder', formValues);

        setLoading(false);
        setUpdated(true);
        setTimeout(() => setUpdated(false), 3000);

        await Promise.resolve(fetchAllFoldersAndInstallations(product));

        setLoading(false);
      } catch (error) {
        setError(true);
        if (error.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
      }
    },
    [fetchAllFoldersAndInstallations, navigate, removeToken]
  );

  const deleteFolder = useCallback(
    async (formValues: I_Folder, product: number) => {
      try {
        const res = await axiosConfig.delete(
          `/DeleteFolder?folderId=${formValues.id}`
        );
        setUpdated(true);
        setLoading(false);
        await Promise.resolve(fetchAllFoldersAndInstallations(product));
        setTimeout(() => setUpdated(false), 3000);
      } catch (error) {
        setError(true);
        if (error.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
      }
    },
    [fetchAllFoldersAndInstallations, navigate, removeToken]
  );

  const moveInstallation = useCallback(
    async (installationId: number, parentId: number, product: number) => {
      try {
        await axiosConfig.put(
          `/MoveInstallation?installationId=${installationId}&parentId=${parentId}`
        );
        await fetchAllFoldersAndInstallations(product);
      } catch (error) {
        setError(true);
        if (error.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
      }
    },
    [fetchAllFoldersAndInstallations, navigate, removeToken]
  );

  const moveFolder = useCallback(
    async (folderId: number, parentId: number, product: number) => {
      try {
        await axiosConfig.put(
          `/MoveFolder?folderId=${folderId}&parentId=${parentId}`
        );
        await fetchAllFoldersAndInstallations(product);
      } catch (error) {
        setError(true);
        if (error.response?.status === 401) {
          removeToken();
          navigate(routes.login);
        }
      }
    },
    [fetchAllFoldersAndInstallations, navigate, removeToken]
  );

  const contextValue = useMemo(
    () => ({
      salimax_or_sodistore_Installations,
      salidomoInstallations,
      sodiohomeInstallations,
      sodistoreGridInstallations,
      sodistoreProInstallations,
      foldersAndInstallations,
      fetchAllInstallations,
      fetchAllFoldersAndInstallations,
      createInstallation,
      updateInstallation,
      loading,
      setLoading,
      error,
      setError,
      updated,
      setUpdated,
      deleteInstallation,
      createFolder,
      updateFolder,
      deleteFolder,
      moveInstallation,
      moveFolder,
      //currentProduct,
      socket,
      openSocket,
      closeSocket
    }),
    [
      salimax_or_sodistore_Installations,
      salidomoInstallations,
      sodiohomeInstallations,
      sodistoreGridInstallations,
      sodistoreProInstallations,
      foldersAndInstallations,
      loading,
      error,
      updated,
      socket
    ]
  );

  return (
    <InstallationsContext.Provider value={contextValue}>
      {children}
    </InstallationsContext.Provider>
  );
};

export default InstallationsContextProvider;
